import { ArrowRight, Loader2, Phone, Voicemail } from "lucide-react";
import { Link } from "react-router-dom";
import { useListenerPhoneCalls, type ListenerPhoneCallMetadata } from "@/hooks/phone";

interface ListenerPhoneCallsProps {
  agentId: string;
}

const STATUS_LABELS: Record<NonNullable<ListenerPhoneCallMetadata["status"]>, string> = {
  processing: "Analysing",
  completed: "Analysed",
  failed: "Failed",
};

const formatDuration = (seconds?: number) =>
  seconds ? `${Math.floor(seconds / 60)}:${String(seconds % 60).padStart(2, "0")}` : "—";

const ListenerPhoneCalls = ({ agentId }: ListenerPhoneCallsProps) => {
  const { calls, isLoading, error } = useListenerPhoneCalls(agentId);

  if (isLoading) {
    return (
      <div className="flex justify-center py-12">
        <Loader2 className="h-6 w-6 animate-spin text-genie-primary" />
      </div>
    );
  }

  if (error) {
    return <p className="py-6 text-sm text-red-600">Failed to load calls. Please try again.</p>;
  }

  if (calls.length === 0) {
    return (
      <div className="flex flex-col items-center gap-2 px-4 py-10 bg-gray-50 rounded-lg border-2 border-dashed border-gray-300 text-center">
        <Phone className="h-5 w-5 text-gray-400" />
        <p className="text-sm text-gray-500">No calls yet. Calls to this number appear here once they end.</p>
      </div>
    );
  }

  return (
    <div className="space-y-2 pb-4">
      {calls.map((call) => {
        const metadata: ListenerPhoneCallMetadata = call.metadata ?? {};
        const Icon = metadata.kind === "voicemail" ? Voicemail : Phone;

        return (
          <Link
            key={call.id}
            to={`/genie-hub/conversations/${call.id}`}
            className="flex items-center justify-between gap-3 px-4 py-3 bg-gray-50 rounded-lg border border-gray-200 hover:bg-white hover:border-gray-300 transition-colors"
          >
            <div className="flex items-center gap-3 min-w-0">
              <Icon className="h-4 w-4 flex-shrink-0 text-genie-primary" />
              <div className="min-w-0">
                <p className="text-sm font-medium text-gray-900 truncate">
                  {metadata.from || "Unknown caller"}
                  {metadata.kind === "voicemail" && <span className="ml-2 text-xs text-gray-500">Voicemail</span>}
                </p>
                <p className="text-xs text-gray-500">
                  {new Date(call.created_at).toLocaleString()} · {formatDuration(metadata.duration_seconds)}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2 flex-shrink-0">
              <span className="text-xs text-gray-500">{STATUS_LABELS[metadata.status ?? "processing"]}</span>
              <ArrowRight className="h-4 w-4 text-gray-400" />
            </div>
          </Link>
        );
      })}
    </div>
  );
};

export default ListenerPhoneCalls;
